import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Zap, Users, Briefcase, Package, DollarSign, Calendar, Building2 } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const ERPPlatforms = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">ERP Platform Solutions</h1>
            <p className="text-xl text-blue-100">
              Comprehensive business management with Zoho, Odoo, and Microsoft Dynamics 365
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center mb-12">
            <p className="text-xl text-gray-700 leading-relaxed">
              As certified implementation partners for leading ERP platforms, we help businesses choose and deploy the right solution for their unique needs. Each platform offers comprehensive business applications covering CRM, Finance, Inventory, Projects, HR, and more, with extensive third-party integrations to create a complete business ecosystem.
            </p>
          </div>
        </div>
      </section>

      {/* Zoho Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full font-semibold mb-4">Zoho Suite</div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Zoho One - 55+ Integrated Apps</h2>
              <p className="text-xl text-gray-600">Complete cloud-based business suite for SMEs to large enterprises</p>
            </div>

            {/* Core Applications Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center mb-4 mx-auto">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">CRM & Sales</h3>
                  <p className="text-sm text-gray-600">Zoho CRM, Bigin, SalesIQ, Voice</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center mb-4 mx-auto">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Finance</h3>
                  <p className="text-sm text-gray-600">Books, Invoice, Expense, Inventory, Billing</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mb-4 mx-auto">
                    <Calendar className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Projects</h3>
                  <p className="text-sm text-gray-600">Zoho Projects, Sprints</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center mb-4 mx-auto">
                    <Briefcase className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">HR & People</h3>
                  <p className="text-sm text-gray-600">People, Recruit, Payroll</p>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Applications Accordion */}
            <Card className="border-none shadow-xl bg-white mb-8">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Complete Zoho Application Suite</h3>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="zoho-crm">
                    <AccordionTrigger className="text-lg font-semibold">CRM & Sales Applications</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Zoho CRM:</strong> Complete sales pipeline management, lead tracking, opportunity management, AI-powered lead scoring, activity scheduling, sales forecasting, and workflow automation</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Bigin:</strong> Simplified CRM for small businesses with pipeline management and contact organization</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>SalesIQ:</strong> Live chat, visitor tracking, and customer engagement analytics</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Desk:</strong> Customer support ticketing system with multi-channel support</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="zoho-finance">
                    <AccordionTrigger className="text-lg font-semibold">Finance & Accounting</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Zoho Books:</strong> Complete accounting solution with invoicing, expense tracking, banking, tax compliance, GST/VAT, financial reports, and automated workflows</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Zoho Invoice:</strong> Professional invoicing with payment gateway integration and client portal</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Zoho Expense:</strong> Expense report automation, receipt scanning, and reimbursement management</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Zoho Inventory:</strong> Stock management, order fulfillment, warehouse tracking, and multi-channel sales integration</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="zoho-projects">
                    <AccordionTrigger className="text-lg font-semibold">Project Management</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Zoho Projects:</strong> Task management, Gantt charts, time tracking, resource allocation, milestones, project collaboration, and reporting</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Zoho Sprints:</strong> Agile project management with scrum boards, sprint planning, and velocity tracking</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="zoho-hr">
                    <AccordionTrigger className="text-lg font-semibold">HR & People Management</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Zoho People:</strong> Complete HRMS with employee database, attendance tracking, leave management, timesheets, appraisals, organizational charts, and workforce analytics</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Zoho Recruit:</strong> Applicant tracking system (ATS) with job posting, resume parsing, interview scheduling</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Zoho Payroll:</strong> Salary processing, tax calculations, compliance, and pay slip generation</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="zoho-integrations">
                    <AccordionTrigger className="text-lg font-semibold">Third-Party Integrations</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <Zap className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Microsoft Services:</strong> Teams, Outlook, Calendar, Azure AD, Office 365 integration</div>
                        </div>
                        <div className="flex items-start">
                          <Zap className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Google Workspace:</strong> Gmail, Google Calendar, Drive, Contacts sync</div>
                        </div>
                        <div className="flex items-start">
                          <Zap className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Collaboration Tools:</strong> Slack, Trello, Asana integration</div>
                        </div>
                        <div className="flex items-start">
                          <Zap className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Payment Gateways:</strong> Stripe, PayPal, Razorpay, Square</div>
                        </div>
                        <div className="flex items-start">
                          <Zap className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Accounting Software:</strong> QuickBooks, Xero integration</div>
                        </div>
                        <div className="flex items-start">
                          <Zap className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Automation:</strong> Zoho Flow (native automation), Zapier, Make (Integromat)</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Odoo Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full font-semibold mb-4">Odoo ERP</div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Odoo - All-in-One Business Suite</h2>
              <p className="text-xl text-gray-600">Open-source ERP with modular design for ultimate flexibility</p>
            </div>

            {/* Core Modules Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center mb-4 mx-auto">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">CRM & Sales</h3>
                  <p className="text-sm text-gray-600">Pipeline, Quotations, Orders</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center mb-4 mx-auto">
                    <Package className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Inventory</h3>
                  <p className="text-sm text-gray-600">Warehouse, Stock, Shipping</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mb-4 mx-auto">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Accounting</h3>
                  <p className="text-sm text-gray-600">Invoicing, Payments, Reports</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center mb-4 mx-auto">
                    <Building2 className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Manufacturing</h3>
                  <p className="text-sm text-gray-600">MRP, Production, Quality</p>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Modules Accordion */}
            <Card className="border-none shadow-xl bg-white mb-8">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Complete Odoo Module Suite</h3>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="odoo-sales">
                    <AccordionTrigger className="text-lg font-semibold">CRM & Sales Management</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>CRM:</strong> Lead and opportunity pipeline, activity scheduling, AI lead scoring, lost reason analysis</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Sales:</strong> Quotations, sales orders, product variants, pricing rules, discount management, subscription billing</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Point of Sale (POS):</strong> Retail sales, restaurant management, inventory sync</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="odoo-inventory">
                    <AccordionTrigger className="text-lg font-semibold">Inventory & Supply Chain</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Warehouse Management:</strong> Multi-warehouse support, stock tracking, barcode scanning, batch/serial numbers</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Purchase:</strong> RFQs, purchase orders, vendor management, purchase agreements</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Shipping:</strong> Integration with shipping carriers, delivery orders, routes optimization</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="odoo-manufacturing">
                    <AccordionTrigger className="text-lg font-semibold">Manufacturing (MRP)</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Production Planning:</strong> Bills of materials (BOM), work orders, routing, master production scheduling</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Quality Control:</strong> Quality checks, control points, alerts</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Maintenance:</strong> Equipment maintenance, preventive maintenance scheduling</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="odoo-accounting">
                    <AccordionTrigger className="text-lg font-semibold">Accounting & Finance</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Core Accounting:</strong> General ledger, journal entries, bank reconciliation, multi-currency</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Invoicing:</strong> Customer invoices, vendor bills, payment terms, recurring invoices</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Financial Reporting:</strong> P&L, balance sheet, tax reports, custom reports</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="odoo-hr">
                    <AccordionTrigger className="text-lg font-semibold">Human Resources</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Employee Management:</strong> Employee directory, contracts, organizational charts</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Time Tracking:</strong> Attendance, leaves, timesheets, work hours</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Recruitment:</strong> Job postings, applicant tracking, interview scheduling</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Appraisals:</strong> Performance reviews, feedback, employee assessment</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="odoo-integrations">
                    <AccordionTrigger className="text-lg font-semibold">Third-Party Integrations</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <Zap className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>E-commerce Marketplaces:</strong> Amazon, eBay, Shopify connectors</div>
                        </div>
                        <div className="flex items-start">
                          <Zap className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Payment Gateways:</strong> Stripe, PayPal, Authorize.net, Adyen</div>
                        </div>
                        <div className="flex items-start">
                          <Zap className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Shipping Carriers:</strong> FedEx, UPS, DHL, USPS integration</div>
                        </div>
                        <div className="flex items-start">
                          <Zap className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Email & Communication:</strong> Gmail, Outlook, VoIP systems</div>
                        </div>
                        <div className="flex items-start">
                          <Zap className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Odoo Apps Store:</strong> 1000+ community and commercial apps for specialized needs</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Microsoft Dynamics 365 Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full font-semibold mb-4">Microsoft Dynamics 365</div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Microsoft Dynamics 365</h2>
              <p className="text-xl text-gray-600">Enterprise-grade ERP and CRM with Power Platform integration</p>
            </div>

            {/* Product Lines Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center mb-4 mx-auto">
                    <Building2 className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Business Central</h3>
                  <p className="text-sm text-gray-600">SMB ERP Solution</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center mb-4 mx-auto">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Finance</h3>
                  <p className="text-sm text-gray-600">Financial Management</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center mb-4 mx-auto">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Sales & Service</h3>
                  <p className="text-sm text-gray-600">CRM Solutions</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-lg flex items-center justify-center mb-4 mx-auto">
                    <Package className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Supply Chain</h3>
                  <p className="text-sm text-gray-600">SCM Operations</p>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Applications Accordion */}
            <Card className="border-none shadow-xl bg-white mb-8">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Microsoft Dynamics 365 Solutions</h3>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="d365-business-central">
                    <AccordionTrigger className="text-lg font-semibold">Business Central (SMB ERP)</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Financial Management:</strong> General ledger, accounts payable/receivable, bank reconciliation, budgeting, cash flow forecasting, fixed assets, subscription billing</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Sales & Marketing:</strong> Opportunity tracking, quote management, order processing</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Supply Chain:</strong> Inventory management, warehouse operations, purchasing</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Project Management:</strong> Job costing, time sheets, resource allocation</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Manufacturing (Premium):</strong> Production planning, routing, capacity planning</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="d365-sales">
                    <AccordionTrigger className="text-lg font-semibold">Dynamics 365 Sales</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Sales Automation:</strong> Lead qualification, opportunity management, guided selling</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Relationship Intelligence:</strong> AI-driven insights, conversation intelligence</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Pipeline Management:</strong> Deal tracking, forecasting, sales analytics</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="d365-customer-service">
                    <AccordionTrigger className="text-lg font-semibold">Dynamics 365 Customer Service</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Case Management:</strong> Multi-channel support, case routing, SLA management</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Knowledge Base:</strong> Self-service portal, AI-powered article suggestions</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Omnichannel:</strong> Chat, email, social media, phone integration</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="d365-field-service">
                    <AccordionTrigger className="text-lg font-semibold">Dynamics 365 Field Service</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Work Order Management:</strong> Scheduling, dispatching, mobile access</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Resource Optimization:</strong> AI-powered scheduling, route optimization</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Asset Management:</strong> IoT integration, predictive maintenance</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="d365-finance-ops">
                    <AccordionTrigger className="text-lg font-semibold">Dynamics 365 Finance & Supply Chain</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Finance:</strong> Advanced financial management, compliance, asset management</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Supply Chain:</strong> Inventory optimization, warehouse management, transportation</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Manufacturing:</strong> Discrete and process manufacturing, production control</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="d365-hr">
                    <AccordionTrigger className="text-lg font-semibold">Dynamics 365 Human Resources</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Core HR:</strong> Employee lifecycle management, organizational management</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Benefits:</strong> Benefits administration, enrollment, life events</div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Leave & Absence:</strong> Time off tracking, accruals, approvals</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="d365-integrations">
                    <AccordionTrigger className="text-lg font-semibold">Third-Party Integrations & Ecosystem</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start">
                          <Zap className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Microsoft 365:</strong> Teams, Outlook, Excel, Word, SharePoint deep integration</div>
                        </div>
                        <div className="flex items-start">
                          <Zap className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Power Platform:</strong> Power Apps (custom apps), Power Automate (workflows), Power BI (analytics)</div>
                        </div>
                        <div className="flex items-start">
                          <Zap className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Azure Services:</strong> Azure AI, IoT, data services integration</div>
                        </div>
                        <div className="flex items-start">
                          <Zap className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>AppSource Marketplace:</strong> 1000+ business apps from Microsoft partners</div>
                        </div>
                        <div className="flex items-start">
                          <Zap className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div><strong>Payment & Banking:</strong> Multiple payment gateways and banking connectors</div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Choosing the Right Platform</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-2 border-orange-200 shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-orange-600 mb-4">Zoho One</h3>
                  <ul className="space-y-2 text-gray-700 mb-6">
                    <li>• Best for: SMEs to mid-market</li>
                    <li>• 55+ apps, all-inclusive</li>
                    <li>• Cloud-based, quick deployment</li>
                    <li>• Affordable pricing model</li>
                    <li>• Excellent for multi-department needs</li>
                  </ul>
                  <Link to="/contact">
                    <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white">Learn More</Button>
                  </Link>
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-200 shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-purple-600 mb-4">Odoo</h3>
                  <ul className="space-y-2 text-gray-700 mb-6">
                    <li>• Best for: Startups to enterprises</li>
                    <li>• Open-source flexibility</li>
                    <li>• Modular, pay for what you use</li>
                    <li>• Highly customizable</li>
                    <li>• Strong manufacturing features</li>
                  </ul>
                  <Link to="/contact">
                    <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white">Learn More</Button>
                  </Link>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-blue-600 mb-4">Microsoft D365</h3>
                  <ul className="space-y-2 text-gray-700 mb-6">
                    <li>• Best for: Mid to large enterprises</li>
                    <li>• Enterprise-grade capabilities</li>
                    <li>• Microsoft ecosystem integration</li>
                    <li>• AI and Power Platform</li>
                    <li>• Industry-specific solutions</li>
                  </ul>
                  <Link to="/contact">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">Learn More</Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Choose Your ERP Platform?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-blue-100">
            Our certified consultants will help you select and implement the perfect ERP solution for your business
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6">
              Schedule a Consultation <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ERPPlatforms;